/**
 * AcceptQuoteModal - Full form for accepting a transfer bond quote
 * Ported from legacy quoteDetailModal.jsx with all 50+ fields,
 * county-specific required fields, underwriting authority check,
 * and per-power bond entries.
 */

import { useReducer, useEffect, useRef, useState, useMemo } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { getUnderwritingAuthority } from '../../../../services/oathTransferBondService';
import usePermissions from '../../../../hooks/usePermissions';
import { getRequiredFields, FIELD_LABELS } from '../shared/countyRequiredFields';
import { formatCurrency, calculateTotalFees } from '../shared/formatUtils';
import {
  OathModal,
  OathButton,
  OathAlert,
  OathSpinner,
} from '../../../shared/OathUI';

import DefendantInfoSection from './AcceptQuoteForm/DefendantInfoSection';
import DefendantAddressSection from './AcceptQuoteForm/DefendantAddressSection';
import PhysicalDescriptionSection from './AcceptQuoteForm/PhysicalDescriptionSection';
import NextOfKinSection from './AcceptQuoteForm/NextOfKinSection';
import NearestRelativeSection from './AcceptQuoteForm/NearestRelativeSection';
import BondPowersSection from './AcceptQuoteForm/BondPowersSection';
import AdditionalInfoSection from './AcceptQuoteForm/AdditionalInfoSection';

// Initial form state
const INITIAL_STATE = {
  firstName: '', middleInitial: '', lastName: '',
  totalBondAmount: '', dateOfBirth: '', bookingNumber: '',
  driversLicenseNumber: '', driversLicenseState: '',
  race: '', gender: '', placeOfBirth: '',
  socialSecurityNumber: '', arrestDate: '',
  jailPhoneNumber: '', jailInfo: '',
  courtCode: '', courtPlace: '',
  defendantStreet: '', defendantCity: '',
  defendantState: '', defendantZipCode: '',
  defendantPhoneNumber: '',
  hairColor: '', eyeColor: '', weight: '', height: '',
  xrefNumber: '', pfnNumber: '',
  nextOfKinName: '', nextOfKinPhoneNumber: '',
  nextOfKinAddressStreet1: '', nextOfKinAddressStreet2: '',
  nextOfKinAddressCity: '', nextOfKinAddressState: '',
  nextOfKinAddressZipCode: '',
  nearestRelativeName: '',
  nearestRelativeAddressStreet1: '', nearestRelativeAddressStreet2: '',
  nearestRelativeAddressCity: '', nearestRelativeAddressState: '',
  nearestRelativeAddressZipCode: '',
  powers: [],
  indemnitorName: '', indemnitorPhone: '', additionalInfo: '',
};

function formReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'UPDATE_POWER':
      return {
        ...state,
        powers: state.powers.map((p) =>
          p.id === action.powerId ? { ...p, [action.field]: action.value } : p
        ),
      };
    case 'INIT':
      return { ...INITIAL_STATE, ...action.payload };
    case 'RESET':
      return { ...INITIAL_STATE };
    default:
      return state;
  }
}

const AcceptQuoteModal = ({ isOpen, onClose, onConfirm, onDecline, quote, bondDetails, isSubmitting }) => {
  const { canAccessContract } = usePermissions();
  const [formState, dispatch] = useReducer(formReducer, INITIAL_STATE);
  const [validationError, setValidationError] = useState('');
  const [uwaError, setUwaError] = useState('');
  const [checkingUwa, setCheckingUwa] = useState(false);

  const requiredFields = useMemo(
    () => getRequiredFields(bondDetails?.countyId),
    [bondDetails?.countyId]
  );

  const totalFees = quote ? calculateTotalFees(quote) : 0;

  // Check if power bond amounts sum to the total bond amount
  const { powerTotal, amountsMatch } = useMemo(() => {
    const sum = formState.powers.reduce(
      (acc, p) => acc + (parseFloat(p.bondAmount) || 0),
      0
    );
    const expected = parseFloat(formState.totalBondAmount) || 0;
    return {
      powerTotal: sum,
      amountsMatch: expected > 0 && Math.abs(sum - expected) < 0.01,
    };
  }, [formState.powers, formState.totalBondAmount]);

  // Track whether we've already initialized for this modal session
  const initializedRef = useRef(false);

  // Initialize form from bondDetails ONCE when modal opens (matches legacy behavior)
  useEffect(() => {
    if (isOpen && bondDetails && !initializedRef.current) {
      initializedRef.current = true;
      const numberOfBonds = bondDetails.numberOfBonds || 1;
      const powers = [];
      for (let i = 0; i < numberOfBonds; i++) {
        powers.push({
          id: i,
          bondAmount: 0,
          offense: '',
          offenseType: '',
          caseNumber: '',
          courtPlace: bondDetails.courtPlace || '',
          courtDate: '',
        });
      }

      dispatch({
        type: 'INIT',
        payload: {
          firstName: bondDetails.defendantFirstName || '',
          lastName: bondDetails.defendantLastName || '',
          totalBondAmount: bondDetails.totalBondAmount?.toString() || '',
          courtPlace: bondDetails.courtPlace || '',
          powers,
        },
      });
      setValidationError('');
      setUwaError('');
    }

    // Reset the flag when modal closes so next open re-initializes
    if (!isOpen) {
      initializedRef.current = false;
    }
  }, [isOpen, bondDetails]);

  // Validate form
  const validate = () => {
    // Check required fields
    for (const field of requiredFields) {
      const value = formState[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        setValidationError(`${FIELD_LABELS[field] || field} is required.`);
        return false;
      }

      if (field === 'totalBondAmount' && parseFloat(value) <= 0) {
        setValidationError('Total Bond Amount must be greater than 0.');
        return false;
      }

      // Zip code validation
      if (['defendantZipCode', 'nearestRelativeAddressZipCode', 'nextOfKinAddressZipCode'].includes(field)) {
        if (!/^\d{5}$/.test(value)) {
          setValidationError(`${FIELD_LABELS[field]} must be a 5-digit number.`);
          return false;
        }
      }

      // Phone validation
      if (['defendantPhoneNumber', 'nextOfKinPhoneNumber', 'jailPhoneNumber'].includes(field)) {
        if (!/^\d+$/.test(value)) {
          setValidationError(`${FIELD_LABELS[field]} must contain only numbers.`);
          return false;
        }
      }

      // Numeric validation
      if (['weight', 'height'].includes(field)) {
        if (!/^\d+$/.test(value)) {
          setValidationError(`${FIELD_LABELS[field]} must be a number.`);
          return false;
        }
      }
    }

    // Validate powers
    for (const power of formState.powers) {
      if (!(parseFloat(power.bondAmount) > 0)) {
        setValidationError(`Bond amount is required for Power #${power.id + 1}.`);
        return false;
      }
      if (!power.offense) {
        setValidationError(`Offense is required for Power #${power.id + 1}.`);
        return false;
      }
      if (requiredFields.includes('chargeType') && !power.offenseType) {
        setValidationError(`Offense Type is required for Power #${power.id + 1}.`);
        return false;
      }
      if (requiredFields.includes('courtDateFound') && !power.courtDate) {
        setValidationError(`Court Date is required for Power #${power.id + 1}.`);
        return false;
      }
    }

    setValidationError('');
    return true;
  };

  // Handle submit with UWA check
  const handleSubmit = async () => {
    if (!validate()) return;

    // Underwriting authority check — only when user has contract visibility
    if (canAccessContract) {
      setCheckingUwa(true);
      setUwaError('');
      try {
        const uwaResponse = await getUnderwritingAuthority(bondDetails.requestingAgentID);
        if (uwaResponse.success) {
          const uwa = parseFloat(uwaResponse.data);
          const totalBondAmount = parseFloat(formState.totalBondAmount);
          if (totalBondAmount > uwa && !bondDetails.authorizedBy) {
            setUwaError('This bond amount is over your underwriting authority. Please call Bankers Surety for assistance.');
            setCheckingUwa(false);
            return;
          }
        }
      } catch {
        // If UWA check fails, allow submission to continue
      }
      setCheckingUwa(false);
    }

    // Build payload matching legacy format
    const payload = {
      quoteId: quote?.transferBondQuoteID || 0,
      firstName: formState.firstName.trim(),
      middleInitial: formState.middleInitial.trim(),
      lastName: formState.lastName.trim(),
      totalBondAmount: parseFloat(formState.totalBondAmount) || 0,
      dateOfBirth: formState.dateOfBirth ? new Date(formState.dateOfBirth).toISOString() : null,
      bookingNumber: formState.bookingNumber.trim(),
      driversLicenseNumber: formState.driversLicenseNumber.trim(),
      driversLicenseState: formState.driversLicenseState,
      race: formState.race.trim(),
      gender: formState.gender,
      arrestDate: formState.arrestDate ? new Date(formState.arrestDate).toISOString() : null,
      courtCode: formState.courtCode.trim(),
      jailPhoneNumber: formState.jailPhoneNumber.trim(),
      jailInfo: formState.jailInfo.trim(),
      city: formState.defendantCity.trim(),
      placeOfBirth: formState.placeOfBirth.trim(),
      socialSecurityNumber: formState.socialSecurityNumber.trim(),
      defendantStreet: formState.defendantStreet.trim(),
      defendantCity: formState.defendantCity.trim(),
      defendantState: formState.defendantState,
      defendantZipCode: formState.defendantZipCode.trim(),
      defendantPhoneNumber: formState.defendantPhoneNumber.trim(),
      hairColor: formState.hairColor.trim(),
      eyeColor: formState.eyeColor.trim(),
      weight: formState.weight ? parseInt(formState.weight) : null,
      height: formState.height ? parseInt(formState.height) : null,
      nearestRelativeName: formState.nearestRelativeName.trim(),
      nearestRelativeAddress: {
        street1: formState.nearestRelativeAddressStreet1.trim(),
        street2: formState.nearestRelativeAddressStreet2.trim(),
        city: formState.nearestRelativeAddressCity.trim(),
        state: formState.nearestRelativeAddressState,
        zipCode: formState.nearestRelativeAddressZipCode.trim(),
      },
      nextOfKinName: formState.nextOfKinName.trim(),
      nextOfKinPhoneNumber: formState.nextOfKinPhoneNumber.trim(),
      nextOfKinAddress: {
        street1: formState.nextOfKinAddressStreet1.trim(),
        street2: formState.nextOfKinAddressStreet2.trim(),
        city: formState.nextOfKinAddressCity.trim(),
        state: formState.nextOfKinAddressState,
        zipCode: formState.nextOfKinAddressZipCode.trim(),
      },
      powers: formState.powers.map((power) => ({
        ...power,
        bondAmount: parseFloat(power.bondAmount) || 0,
        courtDate: power.courtDate ? new Date(power.courtDate).toISOString() : null,
      })),
      xrefNumber: formState.xrefNumber.trim(),
      pfnNumber: formState.pfnNumber.trim(),
      indemnitorName: formState.indemnitorName.trim(),
      indemnitorPhone: formState.indemnitorPhone.trim(),
      additionalInfo: formState.additionalInfo.trim(),
    };

    onConfirm(payload);
  };

  const handleClose = () => {
    dispatch({ type: 'RESET' });
    setValidationError('');
    setUwaError('');
    onClose();
  };

  const handleUpdatePower = (powerId, field, value) => {
    dispatch({ type: 'UPDATE_POWER', powerId, field, value });
  };

  return (
    <OathModal
      isOpen={isOpen && !!quote}
      onClose={handleClose}
      title="Accept Quote"
      size="xl"
      footer={
        <div className="w-full space-y-3">
          {(validationError || uwaError) && (
            <OathAlert variant="error" message={validationError || uwaError} />
          )}
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
            <OathButton
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting || checkingUwa}
              className="w-full sm:w-auto"
            >
              Cancel
            </OathButton>
            {onDecline && (
              <OathButton
                variant="danger"
                onClick={onDecline}
                disabled={isSubmitting || checkingUwa}
                className="w-full sm:w-auto"
              >
                Decline Quote
              </OathButton>
            )}
            <OathButton
              onClick={handleSubmit}
              loading={isSubmitting || checkingUwa}
              disabled={!amountsMatch}
              className="w-full sm:w-auto"
            >
              {checkingUwa ? 'Checking...' : isSubmitting ? 'Accepting...' : 'Accept Quote'}
            </OathButton>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Info bullets */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 text-sm">
          <div className="flex items-start gap-2 mb-2">
            <CheckCircleIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <span className="text-gray-600 dark:text-gray-400">
              By accepting this quote, the posting agent will be notified and begin posting.
            </span>
          </div>
          <div className="flex items-start gap-2 mb-2">
            <CheckCircleIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <span className="text-gray-600 dark:text-gray-400">
              All other quotes will be automatically declined.
            </span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircleIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <span className="text-gray-600 dark:text-gray-400">
              Fill in the required fields for the selected county. Fields marked with * are required.
            </span>
          </div>
        </div>

        {/* Quote summary */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Total Fees: <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(totalFees)}</span>
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Post Time: <span className="font-medium text-gray-900 dark:text-white">
              {quote ? (quote.promisedPostingTimeInMinutes / 60).toFixed(1) : '-'} hours
            </span>
          </p>
        </div>

        {/* Payment warning */}
        {quote?.paymentRequiredUpFront && (
          <OathAlert variant="warning">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">
                This quote requires upfront payment. A Bankers associate will contact you.
              </p>
            </div>
          </OathAlert>
        )}

        {/* Errors */}
        {(validationError || uwaError) && (
          <OathAlert variant="error" message={validationError || uwaError} />
        )}

        {checkingUwa && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <OathSpinner size="sm" /> Checking underwriting authority...
          </div>
        )}

        {/* Form sections */}
        <DefendantInfoSection formState={formState} dispatch={dispatch} requiredFields={requiredFields} />

        <hr className="border-gray-200 dark:border-gray-700" />

        <DefendantAddressSection formState={formState} dispatch={dispatch} requiredFields={requiredFields} />

        <hr className="border-gray-200 dark:border-gray-700" />

        <PhysicalDescriptionSection formState={formState} dispatch={dispatch} requiredFields={requiredFields} />

        <hr className="border-gray-200 dark:border-gray-700" />

        <NextOfKinSection formState={formState} dispatch={dispatch} requiredFields={requiredFields} />

        <hr className="border-gray-200 dark:border-gray-700" />

        <NearestRelativeSection formState={formState} dispatch={dispatch} requiredFields={requiredFields} />

        <hr className="border-gray-200 dark:border-gray-700" />

        <BondPowersSection
          powers={formState.powers}
          onUpdatePower={handleUpdatePower}
          requiredFields={requiredFields}
          powerTotal={powerTotal}
          totalBondAmount={parseFloat(formState.totalBondAmount) || 0}
          amountsMatch={amountsMatch}
        />

        <hr className="border-gray-200 dark:border-gray-700" />

        <AdditionalInfoSection formState={formState} dispatch={dispatch} />
      </div>
    </OathModal>
  );
};

export default AcceptQuoteModal;
